/**
 * SectionCard — the bordered, optionally collapsible workspace section card.
 */
import { useState, type ReactNode } from "react";
import { cx } from "@shared/cx";
import { ChevronDownIcon } from "./icons";
import styles from "./SectionCard.module.css";

interface SectionCardProps {
  title?: string;
  children: ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onToggle?: () => void;
  summary?: string;
  state?: "active" | "complete" | "future";
  sectionRef?: React.RefObject<HTMLDivElement | null>;
  headerAction?: ReactNode;
  isStale?: boolean;
}

export function SectionCard({
  title,
  children,
  className = "",
  collapsible = false,
  defaultOpen = true,
  open: controlledOpen,
  onToggle,
  summary,
  state,
  sectionRef,
  headerAction,
  isStale = false,
}: SectionCardProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  // `active` and `complete` are the card's own border; only `future` differs,
  // so it is the only state with a class of its own.
  const stateStyle = state === "future" ? styles.future : undefined;
  const toggleDisabled = state === "future";
  const isOpen = !collapsible || (open && !toggleDisabled);

  const toggle = () => {
    if (toggleDisabled) return;
    if (isControlled && onToggle) {
      onToggle();
    } else {
      setInternalOpen(!open);
    }
  };

  const staleBadge = isStale ? (
    <span className={styles.staleBadge}>Needs regeneration</span>
  ) : null;
  const bodyClass = title ? styles.body : styles.bodyUntitled;

  return (
    <div ref={sectionRef} className={cx(styles.card, stateStyle, className)}>
      {title && (
        <div className={styles.header}>
          <div className={styles.headerRow}>
            {collapsible ? (
              <h2 className={styles.title}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  disabled={toggleDisabled}
                  onClick={toggle}
                  className={cx(
                    styles.toggle,
                    toggleDisabled && styles.toggleDisabled,
                  )}
                >
                  {title}
                  {staleBadge}
                  <ChevronDownIcon
                    className={cx(styles.chevron, isOpen && styles.chevronOpen)}
                  />
                </button>
              </h2>
            ) : (
              <h2 className={cx(styles.title, styles.titleStatic)}>
                {title}
                {staleBadge}
              </h2>
            )}
            {headerAction && (
              <span className={styles.headerAction}>{headerAction}</span>
            )}
          </div>
          {!isOpen && summary && <p className={styles.summary}>{summary}</p>}
        </div>
      )}
      {isOpen &&
        (state === "future" ? (
          // Why the section is inert is a fact the operator needs, so it is on
          // the page, not behind a hover — the old tooltip could never open
          // anyway: its trigger was this pointer-events-none block.
          <div className={bodyClass}>
            <p className={styles.lockedNote}>
              Locked until the workflow reaches this step.
            </p>
            <div className={styles.inert} aria-disabled>
              {children}
            </div>
          </div>
        ) : (
          <div className={bodyClass}>{children}</div>
        ))}
    </div>
  );
}
